import React, { useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaLandmark } from "react-icons/fa";
import { FiSun, FiMoon } from "react-icons/fi";
import { useTheme } from "../../context/ThemeContext";
import useComingSoonToast from "../../hooks/useComingSoonToast";

const SPORT_ICONS = {
  cricket: require("../../assets/img/icons/Cricket Icon.png"),
  soccer: require("../../assets/img/icons/Soccer Icon.png"),
  tennis: require("../../assets/img/icons/Tennis Icon.png"),
};

const getSportNavIcon = (name) => {
  try { return require(`../../assets/img/icons/${name} Icon.png`); }
  catch { return null; }
};

const MobileHeader = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.account.userData);
  const [isDropMenuOpen, setIsDropMenuOpen] = React.useState(false);
  const { theme, toggleTheme } = useTheme();
  const dropdownRef = useRef(null);
  const showComingSoon = useComingSoonToast();

  const casinoNavTabs = [
    { name: "Crash", pending: true, icon: require("../../assets/img/aviator-logo.png") },
    { name: "Lottery", pending: true },
    { name: "Sports", href: "/" },
    { name: "Our Casino", href: "/live-casino" },
    { name: "Live Casino", href: "/live-casino" },
    { name: "Slots", href: "/slots" },
    { name: "Fantasy", href: "/fantasy" },
  ];

  const sportNavTabs = [
    { name: "Cricket", iconName: "Cricket", id: "cricket", href: "/" },
    { name: "Politics", id: "politics", pending: true },
    { name: "Football", iconName: "Soccer", id: "soccer", href: "/games/soccer" },
    { name: "Tennis", iconName: "Tennis", id: "tennis", href: "/games/tennis" },
    { name: "Table Tennis", iconName: "Table Tennis", id: "table_tennis", pending: true },
    { name: "Esoccer", iconName: "E-Games", id: "esoccer", pending: true },
    { name: "Horse Racing", iconName: "Horse Racing", id: "horse_racing", pending: true },
    { name: "Greyhound Racing", id: "greyhound_racing", pending: true },
    { name: "Basketball", iconName: "Basketball", id: "basketball", pending: true },
    { name: "Wrestling", id: "wrestling", pending: true },
    { name: "Volleyball", iconName: "Volleyball", id: "volleyball", pending: true },
    { name: "Badminton", id: "badminton", pending: true },
    { name: "Snooker", iconName: "Snooker", id: "snooker", pending: true },
  ];

  const cricketGames = useSelector((state) => state?.gameData?.cricketGames);
  const soccerGames = useSelector((state) => state?.gameData?.soccerGames);
  const tennisGames = useSelector((state) => state?.gameData?.tennisGames);
  const cricketOdds = useSelector((state) => state?.gameData?.cricketOdds);
  const soccerOdds = useSelector((state) => state?.gameData?.soccerOdds);
  const tennisOdds = useSelector((state) => state?.gameData?.tennisOdds);

  const tickerMatches = useMemo(() => {
    const hasOdds = (game, sport) => {
      const store = sport === "cricket" ? cricketOdds : sport === "soccer" ? soccerOdds : tennisOdds;
      const odds = store?.[game[sport + "Id"]];
      const mo = sport === "cricket" ? odds?.matchOdds : odds?.matchOddsResponseDTO;
      const r = mo?.[0]?.oddDetailsDTOS;
      return r && r.length > 0 && r.some((x) => x.back1 > 0);
    };
    const pick = (arr, sport, n) => {
      if (!Array.isArray(arr)) return [];
      return arr.filter((g) => hasOdds(g, sport)).map((g) => ({ ...g, sport }))
        .sort((a, b) => (b.inPlay ? 1 : 0) - (a.inPlay ? 1 : 0)).slice(0, n);
    };
    const picked = [...pick(cricketGames, "cricket", 5)];
    if (picked.length < 5) {
      const ids = new Set(picked.map((m) => m.eventName));
      const rest = [...(Array.isArray(cricketGames) ? cricketGames.map((g) => ({ ...g, sport: "cricket" })) : [])]
        .filter((m) => !ids.has(m.eventName) && hasOdds(m, m.sport))
        .sort((a, b) => (b.inPlay ? 1 : 0) - (a.inPlay ? 1 : 0));
      picked.push(...rest.slice(0, 5 - picked.length));
    }
    return picked.slice(0, 5);
  }, [cricketGames, soccerGames, tennisGames, cricketOdds, soccerOdds, tennisOdds]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropMenuOpen(false);
      }
    };
    if (isDropMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropMenuOpen]);

  return (
    <header className="mobile-header-fixed">
      <div className="mobile-header-left">
        <button
          className="mobile-hamburger-btn"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label="Toggle sidebar menu"
        >
          <FaBars size={20} />
        </button>

        <a
          href="/"
          className="mobile-header-logo"
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
          }}
        >
          <span style={{ fontSize: "2.5rem", fontWeight: 700, color: "white", letterSpacing: "2px", fontFamily: "'Times New Roman', Georgia, serif" }}>ALL</span>
        </a>
      </div>

      <div className="mobile-header-right" ref={dropdownRef}>
        <div className="mobile-header-info">
          <span className="mobile-bal">Balance:{userData.balance ?? 0}</span>
          <span className="mobile-exp-row">
            <span
              className="mobile-exp"
              onClick={() => navigate("/report/unsetteled-bet")}
            >
              Exp:{userData.liability ?? 0}
            </span>
            <button
              className="mobile-user-trigger"
              onClick={() => setIsDropMenuOpen(!isDropMenuOpen)}
              aria-label="User menu"
              aria-expanded={isDropMenuOpen}
            >
              {userData.username ?? "User"}
              <span className="mobile-chevron">&#9660;</span>
            </button>
          </span>
        </div>

        {isDropMenuOpen && (
          <ul className="mobile-dropdown-menu">
            <li>
              <a href="/report/account-statement">Account Statement</a>
            </li>
            <li>
              <a href="/report/unsetteled-bet">Current Bet</a>
            </li>
            <li>
              <a href="/report/casino-result">Casino Results</a>
            </li>
            <li>
              <a href="/report/changebtnvalue">Set Button Values</a>
            </li>
            <li className="mobile-dropdown-divider" />
            <li>
              <button
                className="dropdown-theme-row"
                onClick={toggleTheme}
              >
                <span className="dropdown-theme-label">Dark Mode</span>
                <span className={`theme-toggle-switch${theme === "dark" ? " active" : ""}`}>
                  <span className="theme-toggle-knob">
                    {theme === "dark" ? <FiSun size={12} /> : <FiMoon size={12} />}
                  </span>
                </span>
              </button>
            </li>
            <li className="mobile-dropdown-divider" />
            <li>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  dispatch({ type: "gameData/logOut" });
                  dispatch({ type: "accountData/logOut" });
                  localStorage.clear();
                  window.location.href = "/";
                }}
              >
                Sign Out
              </a>
            </li>
          </ul>
        )}
      </div>
      {tickerMatches.length > 0 && (
        <div className="mobile-ticker-row">
          {tickerMatches.map((match, i) => (
            <div key={i} className="mobile-ticker-item" onClick={() => navigate(`/game/${match.sport}/${match[match.sport + "Id"]}`)}>
              <img className={`mobile-ticker-icon${match.inPlay ? " ticker-icon-blink" : ""}`} src={SPORT_ICONS[match.sport]} alt="" />
              <span className="mobile-ticker-text">{match.eventName}</span>
            </div>
          ))}
        </div>
      )}
      <nav className="mobile-casino-nav">
        {casinoNavTabs.map((tab) => (
          <button
            key={tab.name}
            className={`mobile-casino-tab${!tab.pending && location.pathname === tab.href ? " active" : ""}`}
            onClick={() => {
              if (tab.pending) {
                showComingSoon(tab.name);
              } else {
                navigate(tab.href);
              }
            }}
          >
            {tab.icon && <img src={tab.icon} alt="" className="mobile-casino-tab-icon" />}
            {tab.name}
          </button>
        ))}
      </nav>
      <nav className="mobile-sport-nav">
        {sportNavTabs.map((tab) => {
          const icon = tab.iconName ? getSportNavIcon(tab.iconName) : null;
          const isActive = !tab.pending && (
            (tab.href === "/" && location.pathname === "/") ||
            (tab.href && tab.href !== "/" && location.pathname === tab.href)
          );
          return (
            <button
              key={tab.id}
              className={`mobile-sport-tab${isActive ? " active" : ""}`}
              onClick={() => {
                if (tab.pending) {
                  showComingSoon(tab.name);
                } else {
                  navigate(tab.href);
                }
              }}
            >
              {tab.id === "politics" ? (
                <FaLandmark className="mobile-sport-tab-icon" />
              ) : icon ? (
                <img src={icon} alt="" className="mobile-sport-tab-icon" />
              ) : null}
              <span className="mobile-sport-tab-label">{tab.name}</span>
            </button>
          );
        })}
      </nav>
    </header>
  );
};

export default MobileHeader;
